'use client';

import * as React from 'react';

import { cn } from '../lib';

import { Box } from './box';
import { HStack } from './stack';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?:
    | 'primary'
    | 'secondary'
    | 'success'
    | 'warning'
    | 'danger'
    | 'outline'
    | 'outline-success'
    | 'outline-danger'
    | 'ghost'
    | 'ghost-success'
    | 'ghost-danger'
    | 'link';
  size?:
    | 'xs'
    | 'sm'
    | 'md'
    | 'lg'
    | 'xl'
    | 'icon-xs'
    | 'icon-sm'
    | 'icon-md'
    | 'icon-lg'
    | 'icon-xl';
  isLoading?: boolean;
  loadingText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
  colorPalette?: string;
  touchFriendly?: boolean;
  /** Flex grow value for use in flex containers (e.g., flex={1} to fill space) */
  flex?: number | string;
  /** Render as child element (Radix UI / Chakra UI composition pattern) */
  asChild?: boolean;
}

const buttonBaseStyles = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  whiteSpace: 'nowrap',
  fontWeight: 'medium',
  textDecoration: 'none',
  borderRadius: 'md',
  cursor: 'pointer',
  transition: 'all 0.2s',
  userSelect: 'none',
  outline: '2px solid transparent',
  outlineOffset: '2px',
  border: '1px solid transparent',
  position: 'relative',
} as const;

const disabledStyles = {
  opacity: 0.6,
  cursor: 'not-allowed',
  transform: 'none',
} as const;

const sizeStyles = {
  xs: {
    height: '24px',
    padding: '0 8px',
    fontSize: 'xs',
    gap: '4px',
  },
  sm: {
    height: '32px',
    padding: '0 12px',
    fontSize: 'sm',
    gap: '6px',
  },
  md: {
    height: '40px',
    padding: '0 16px',
    fontSize: 'md',
    gap: '8px',
  },
  lg: {
    height: '48px',
    padding: '0 24px',
    fontSize: 'lg',
    gap: '10px',
  },
  xl: {
    height: '56px',
    padding: '0 32px',
    fontSize: 'xl',
    gap: '12px',
  },
  'icon-xs': {
    height: '24px',
    width: '24px',
    padding: '0',
  },
  'icon-sm': {
    height: '32px',
    width: '32px',
    padding: '0',
  },
  'icon-md': {
    height: '40px',
    width: '40px',
    padding: '0',
  },
  'icon-lg': {
    height: '48px',
    width: '48px',
    padding: '0',
  },
  'icon-xl': {
    height: '56px',
    width: '56px',
    padding: '0',
  },
} as const;

const variantStyles = {
  primary: {
    backgroundColor: '#0066CC',
    color: 'white',
  },
  secondary: {
    backgroundColor: '#6B46C1',
    color: 'white',
  },
  success: {
    backgroundColor: '#00AA55',
    color: 'white',
  },
  warning: {
    backgroundColor: '#FFAA00',
    color: 'white',
  },
  danger: {
    backgroundColor: '#DC2626',
    color: 'white',
  },
  outline: {
    backgroundColor: 'transparent',
    color: '#374151',
    border: '1px solid #E5E7EB',
  },
  'outline-success': {
    backgroundColor: 'transparent',
    color: '#00AA55',
    border: '1px solid #00AA55',
  },
  'outline-danger': {
    backgroundColor: 'transparent',
    color: '#DC2626',
    border: '1px solid #DC2626',
  },
  ghost: {
    backgroundColor: 'transparent',
    color: '#374151',
  },
  'ghost-success': {
    backgroundColor: 'transparent',
    color: '#00AA55',
  },
  'ghost-danger': {
    backgroundColor: 'transparent',
    color: '#DC2626',
  },
  link: {
    backgroundColor: 'transparent',
    color: '#0066CC',
    padding: '0',
    height: 'auto',
    textDecoration: 'underline',
  },
} as const;

/**
 * CSS for hover/focus pseudo-states.
 * Cannot use pseudo-selectors in inline `style={}` — browsers silently ignore them.
 * Injected once into <head> via useInsertionEffect singleton guard.
 */
const BUTTON_CSS = `
.tms-button:hover:not(:disabled) { transform: translateY(-1px); }
.tms-button:focus-visible { outline-color: #3385FF; }
.tms-button[data-variant="primary"]:hover:not(:disabled) { background-color: #0052A3; }
.tms-button[data-variant="secondary"]:hover:not(:disabled) { background-color: #553895; }
.tms-button[data-variant="success"]:hover:not(:disabled) { background-color: #008844; }
.tms-button[data-variant="warning"]:hover:not(:disabled) { background-color: #CC8800; }
.tms-button[data-variant="danger"]:hover:not(:disabled) { background-color: #B91C1C; }
.tms-button[data-variant="outline"]:hover:not(:disabled) { background-color: #F9FAFB; }
.tms-button[data-variant="outline-success"]:hover:not(:disabled) { background-color: #F0FDF4; }
.tms-button[data-variant="outline-danger"]:hover:not(:disabled) { background-color: #FEF2F2; }
.tms-button[data-variant="ghost"]:hover:not(:disabled) { background-color: #F3F4F6; }
.tms-button[data-variant="ghost-success"]:hover:not(:disabled) { background-color: #F0FDF4; }
.tms-button[data-variant="ghost-danger"]:hover:not(:disabled) { background-color: #FEF2F2; }
.tms-button[data-variant="link"]:hover:not(:disabled) { text-decoration: none; }
`;

function useButtonCSS() {
  React.useInsertionEffect(() => {
    if (document.querySelector('style[data-axon-button]')) return;
    const el = document.createElement('style');
    el.setAttribute('data-axon-button', '');
    el.textContent = BUTTON_CSS;
    document.head.appendChild(el);
  }, []);
}

const visualSizes: Record<string, number> = {
  xs: 24,
  sm: 32,
  md: 40,
  lg: 48,
  xl: 56,
  'icon-xs': 24,
  'icon-sm': 32,
  'icon-md': 40,
  'icon-lg': 48,
  'icon-xl': 56,
};

const BUTTON_PROP_EXCLUSIONS = new Set([
  'variant',
  'size',
  'fullWidth',
  'isLoading',
  'loadingText',
  'leftIcon',
  'rightIcon',
  'touchFriendly',
  'colorPalette',
  'flex',
]);

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      fullWidth = false,
      isLoading = false,
      loadingText,
      leftIcon,
      rightIcon,
      children,
      disabled,
      className,
      touchFriendly = true,
      colorPalette: _colorPalette, // Reserved for future use - dynamic color palettes
      flex,
      asChild: _asChild, // Radix UI / Chakra UI composition - consumed but not used directly
      ...restProps
    },
    ref
  ) => {
    const domProps = Object.fromEntries(
      Object.entries(restProps).filter(([key]) => !BUTTON_PROP_EXCLUSIONS.has(key))
    );
    useButtonCSS();

    const isIconButton = typeof size === 'string' && size.startsWith('icon-');

    // Touch-friendly logic for smaller buttons
    const minTouchSize = 44;
    const visualSize = visualSizes[size] || 40;
    const needsTouchPadding = touchFriendly && visualSize < minTouchSize;
    const touchPadding = needsTouchPadding ? (minTouchSize - visualSize) / 2 : 0;

    const isDisabled = disabled || isLoading;

    const styles = {
      ...buttonBaseStyles,
      ...(sizeStyles[size] || sizeStyles.md),
      ...(variantStyles[variant] || variantStyles.primary),
      ...(isDisabled && disabledStyles),
      width: fullWidth
        ? '100%'
        : isIconButton && sizeStyles[size] && 'width' in sizeStyles[size]
          ? (sizeStyles[size] as { width: string }).width
          : undefined,
      flex: flex !== undefined ? flex : undefined,
    };

    const buttonElement = (
      <button
        ref={ref}
        type="button"
        translate="no"
        disabled={isDisabled}
        className={cn('tms-button', className)}
        style={styles}
        data-variant={variant}
        {...domProps}
      >
        {isLoading && loadingText ? (
          loadingText
        ) : !isLoading && (leftIcon || rightIcon) ? (
          <HStack gap={((styles as Record<string, unknown>).gap as string | number) || 2}>
            {leftIcon}
            {!isIconButton && children}
            {rightIcon}
          </HStack>
        ) : (
          children
        )}
      </button>
    );

    // Wrap with touch-friendly padding if needed
    if (needsTouchPadding) {
      return (
        <Box as="span" display="inline-block" position="relative" padding={`${touchPadding}px`}>
          {buttonElement}
          <Box as="span" position="absolute" inset={0} pointerEvents="none" aria-hidden="true" />
        </Box>
      );
    }

    return buttonElement;
  }
);

Button.displayName = 'Button';

export { Button };
